import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '../queries/queryKeys';
import { getMyCustomers } from '../services/registrations/customers';
import { useAuthStore } from '../store/useAuthStore';
import { isAdmin, isCustomer } from '../utils/isProfile';
import {
  getOwnedCustomer,
  mapCustomerToSubscriptionSnapshot,
  setCustomerSubscriptionCache,
  type CustomerSubscriptionSnapshot,
} from '../utils/customerSubscription';

export const CUSTOMER_SUBSCRIPTION_QUERY_KEY = queryKeys.customers.meSubscription;

export function useCustomerSubscription(): {
  subscription: CustomerSubscriptionSnapshot;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
} {
  const user = useAuthStore((state) => state.user);
  const enabled = Boolean(user && isCustomer(user));

  const query = useQuery({
    queryKey: CUSTOMER_SUBSCRIPTION_QUERY_KEY,
    queryFn: getMyCustomers,
    enabled,
    staleTime: 60_000,
  });

  const subscription = useMemo(() => {
    if (!enabled || !user) {
      return mapCustomerToSubscriptionSnapshot(null);
    }

    if (isAdmin(user)) {
      return mapCustomerToSubscriptionSnapshot(null);
    }

    const owned = getOwnedCustomer(query.data, user.id);
    const snapshot = mapCustomerToSubscriptionSnapshot(owned);
    setCustomerSubscriptionCache(snapshot);
    return snapshot;
  }, [enabled, query.data, user]);

  return {
    subscription,
    isLoading: enabled && query.isLoading,
    isFetching: enabled && query.isFetching,
    isError: query.isError,
  };
}
